import { generatePath } from "react-router";

import { LangCode } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithCategory,
	FeatureIslandMainWithCategoryWrapper,
} from "@/components/features/_layouts";

import { useFetchCustomMailSetting } from "../api";
import { useCustomMailSettingForm } from "../hooks";
import { customMailSettingCategoriesAndManagerStore } from "../store";
import { ForCheckInRemind } from "./custom-mail-setting-form/for-check-in-remind";
import { ForCheckOutRemind } from "./custom-mail-setting-form/for-check-out-remind";
import { ForExternal } from "./custom-mail-setting-form/for-external";
import { ForRequestReservation } from "./custom-mail-setting-form/for-request-reservation";
import { ForShopping } from "./custom-mail-setting-form/for-shopping";
import { ForTriggeredByReserve } from "./custom-mail-setting-form/for-triggered-by-reserve";
import { HeaderInner } from "./header-inner";
import { HeaderRightButton } from "./header-right-button";

type Props = {
	hotelID: string;
};

export const CustomMailSettingDefault = ({ hotelID }: Props) => {
	// dataの取得
	const { data } = useFetchCustomMailSetting({ hotelID });

	const hotelEmailAddress = data?.hotel?.email ?? "";

	const { form } = useCustomMailSettingForm({
		hotelID,
		customMailSetting: data?.basicCustomMailSetting ?? undefined,
	});
	const activeLangCode = LangCode.Default;

	const navigator = {
		default: generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.TOP, { hotelID: hotelID }),
		i18n: (langCode: LangCode) =>
			generatePath(PATHS.HOTEL.HOTEL_SETTINGS.MAIL_SETTINGS.I18N, { hotelID: hotelID, langCode }),
	};

	const {
		forReservation,
		forCheckInRemind,
		forCheckOutRemind,
		forRequestReservation,
		forShopping,
		forExternal,
		categoryManagerStore,
	} = customMailSettingCategoriesAndManagerStore;

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader
				title="基本メール設定の編集"
				activeLangCode={activeLangCode}
				navigator={navigator}
				rightChildren={<HeaderRightButton form={form} />}
			/>
			<HeaderInner hotelID={hotelID} />
			<FeatureIslandMainWithCategory categoryManagerStore={categoryManagerStore}>
				{/* 予約：確定・変更・キャンセルメールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forReservation}
					islandTitle="予約：確定・変更・キャンセルメールの内容設定"
					tooltipTitle="ゲストに送信されるメールの内容をカスタマイズできます。<br /> ※このメールを送信オフにすることはできません。"
				>
					<ForTriggeredByReserve form={form} hotelID={hotelID} hotelEmailAddress={hotelEmailAddress} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* チェックイン前：リマインドメールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forCheckInRemind}
					islandTitle="チェックイン前：リマインドメールの内容設定"
					tooltipTitle="チェックイン前にゲストに送信されるメールの内容をカスタマイズできます。"
				>
					<ForCheckInRemind form={form} hotelID={hotelID} hotelEmailAddress={hotelEmailAddress} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* チェックアウト後：ご案内メールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forCheckOutRemind}
					islandTitle="チェックアウト後：ご案内メールの内容設定"
					tooltipTitle="チェックアウト後にゲストに送信されるメールの内容をカスタマイズできます。"
				>
					<ForCheckOutRemind form={form} hotelID={hotelID} hotelEmailAddress={hotelEmailAddress} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* リクエスト予約メールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forRequestReservation}
					islandTitle="リクエスト予約メールの内容設定"
					tooltipTitle="リクエスト予約時にゲストに送信されるメールの内容をカスタマイズできます。<br /> ※このメールを送信オフにすることはできません。"
				>
					<ForRequestReservation form={form} hotelID={hotelID} hotelEmailAddress={hotelEmailAddress} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* 宿泊券：ゲストに送信されるメールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forShopping}
					islandTitle="宿泊券：ゲストに送信されるメールの内容設定"
					tooltipTitle="宿泊券購入時にゲストに送信されるメールの内容をカスタマイズできます。<br /> ※このメールを送信オフにすることはできません。"
				>
					<ForShopping form={form} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* メールの外部リンクに関する設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forExternal}
					islandTitle="メールの外部リンクに関する設定"
					tooltipTitle="お客様に送られるメール => ホテル名(最下部)をクリックした際の遷移先のリンクを自由に設定できます。未設定の場合、マガジン版TOPページのリンクが自動で設定されます。"
				>
					<ForExternal form={form} />
				</FeatureIslandMainWithCategoryWrapper>
			</FeatureIslandMainWithCategory>
		</FeatureIslandContainer>
	);
};
